import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import DialogActionButton from "@excalidraw/excalidraw/components/DialogActionButton";
import { useEffect, useMemo, useState } from "react";

import "./CloudOpenDialog.scss";

import type { CloudDrawing } from "../data/cloudStorage";

const PAGE_SIZE = 8;

const dateFormatter = new Intl.DateTimeFormat("ru-RU", {
  dateStyle: "medium",
  timeStyle: "short",
});

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
};

const formatSize = (size: number) => {
  if (size < 1024) {
    return `${size} Б`;
  }
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} КБ`;
  }
  return `${(size / 1024 / 1024).toFixed(1)} МБ`;
};

export const CloudOpenDialog = ({
  drawings,
  error,
  hasCurrentScene,
  isLoading,
  openingDrawingId,
  onCloseRequest,
  onOpenDrawing,
  onRefresh,
}: {
  drawings: CloudDrawing[];
  error: string | null;
  hasCurrentScene: boolean;
  isLoading: boolean;
  openingDrawingId: string | null;
  onCloseRequest: () => void;
  onOpenDrawing: (drawing: CloudDrawing) => void;
  onRefresh: () => void;
}) => {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [selectedDrawingId, setSelectedDrawingId] = useState<string | null>(
    null,
  );

  const filteredDrawings = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) {
      return drawings;
    }

    return drawings.filter((drawing) =>
      drawing.name.toLocaleLowerCase().includes(normalizedQuery),
    );
  }, [drawings, query]);

  const pageCount = Math.max(1, Math.ceil(filteredDrawings.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleDrawings = filteredDrawings.slice(
    currentPage * PAGE_SIZE,
    currentPage * PAGE_SIZE + PAGE_SIZE,
  );

  const selectedDrawing =
    filteredDrawings.find((drawing) => drawing.id === selectedDrawingId) ||
    visibleDrawings[0] ||
    null;

  useEffect(() => {
    setPage(0);
  }, [query]);

  useEffect(() => {
    if (!filteredDrawings.length) {
      setSelectedDrawingId(null);
      return;
    }

    if (
      !selectedDrawingId ||
      !filteredDrawings.some((drawing) => drawing.id === selectedDrawingId)
    ) {
      setSelectedDrawingId(filteredDrawings[0].id);
    }
  }, [filteredDrawings, selectedDrawingId]);

  const isOpening = Boolean(openingDrawingId);

  return (
    <Dialog
      className="cloud-open-dialog"
      closeOnClickOutside={!isOpening}
      onCloseRequest={onCloseRequest}
      size="wide"
      title="Открыть файл с сервера"
    >
      <div className="cloud-open-dialog__toolbar">
        <input
          aria-label="Поиск по файлам"
          className="cloud-open-dialog__search"
          disabled={isLoading}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Найти файл"
          type="search"
          value={query}
        />
        <DialogActionButton
          label="Обновить"
          disabled={isLoading || isOpening}
          isLoading={isLoading}
          onClick={onRefresh}
        />
      </div>

      {error && <div className="cloud-open-dialog__error">{error}</div>}

      <div className="cloud-open-dialog__content">
        <div className="cloud-open-dialog__list" aria-busy={isLoading}>
          <div className="cloud-open-dialog__list-header">
            <span>Файл</span>
            <span>Изменен</span>
            <span>Размер</span>
          </div>

          {isLoading && !drawings.length ? (
            <div className="cloud-open-dialog__empty">Загружаю файлы...</div>
          ) : visibleDrawings.length ? (
            visibleDrawings.map((drawing) => {
              const isSelected = selectedDrawing?.id === drawing.id;
              return (
                <button
                  aria-pressed={isSelected}
                  className="cloud-open-dialog__row"
                  data-selected={isSelected}
                  disabled={isOpening}
                  key={drawing.id}
                  onClick={() => setSelectedDrawingId(drawing.id)}
                  onDoubleClick={() => onOpenDrawing(drawing)}
                  type="button"
                >
                  <span className="cloud-open-dialog__name">
                    {drawing.name}
                  </span>
                  <span>{formatDate(drawing.updatedAt)}</span>
                  <span>{formatSize(drawing.size)}</span>
                </button>
              );
            })
          ) : (
            <div className="cloud-open-dialog__empty">
              {query.trim()
                ? "По этому запросу файлов нет"
                : "На сервере пока нет файлов"}
            </div>
          )}
        </div>

        <aside className="cloud-open-dialog__details">
          {selectedDrawing ? (
            <>
              <div>
                <div className="cloud-open-dialog__label">Выбранный файл</div>
                <div className="cloud-open-dialog__selected-name">
                  {selectedDrawing.name}
                </div>
              </div>
              <dl>
                <div>
                  <dt>Изменен</dt>
                  <dd>{formatDate(selectedDrawing.updatedAt)}</dd>
                </div>
                <div>
                  <dt>Создан</dt>
                  <dd>{formatDate(selectedDrawing.createdAt)}</dd>
                </div>
                <div>
                  <dt>Размер</dt>
                  <dd>{formatSize(selectedDrawing.size)}</dd>
                </div>
              </dl>
              {hasCurrentScene && (
                <div className="cloud-open-dialog__warning">
                  Текущая доска будет заменена выбранным файлом.
                </div>
              )}
            </>
          ) : (
            <div className="cloud-open-dialog__details-empty">
              Выберите файл в списке
            </div>
          )}
        </aside>
      </div>

      <div className="cloud-open-dialog__footer">
        <div className="cloud-open-dialog__pagination">
          <button
            disabled={currentPage === 0 || isOpening}
            onClick={() => setPage((value) => Math.max(0, value - 1))}
            type="button"
          >
            Назад
          </button>
          <span>
            Страница {currentPage + 1} из {pageCount}
          </span>
          <button
            disabled={currentPage >= pageCount - 1 || isOpening}
            onClick={() =>
              setPage((value) => Math.min(pageCount - 1, value + 1))
            }
            type="button"
          >
            Вперед
          </button>
        </div>

        <div className="cloud-open-dialog__actions">
          <DialogActionButton
            label="Отмена"
            disabled={isOpening}
            onClick={onCloseRequest}
          />
          <DialogActionButton
            actionType="primary"
            disabled={!selectedDrawing || isOpening}
            isLoading={Boolean(
              selectedDrawing && openingDrawingId === selectedDrawing.id,
            )}
            label="Открыть выбранный"
            onClick={() => {
              if (selectedDrawing) {
                onOpenDrawing(selectedDrawing);
              }
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};
